/**
 * Dashboard data fetch hook
 *
 * Provides dashboard data (today's summary and week summary) using TanStack Query
 * with proper caching, loading states, and error handling.
 */

import { useQuery } from "@tanstack/react-query";
import {
  createApiClient,
  ApiClientError,
  type DashboardResponse,
} from "@floline/api-client";
import { API_URL } from "@/env";

/** Query key factory for dashboard */
export const dashboardKeys = {
  all: ["dashboard"] as const,
  summary: () => [...dashboardKeys.all, "summary"] as const,
};

/** API client configured for dashboard requests */
const apiClient = createApiClient(
  API_URL
);

/**
 * Fetch dashboard data from the API
 *
 * @returns Dashboard data with today's summary and week summary
 */
async function fetchDashboard(): Promise<DashboardResponse> {
  const response = await apiClient.getDashboard();
  return response.data;
}

/**
 * Hook to fetch dashboard data
 *
 * Returns dashboard data along with loading and error states.
 *
 * @example
 * ```tsx
 * function DashboardPage() {
 *   const { data, isLoading, error, refetch } = useDashboard();
 *
 *   if (isLoading) return <Spinner />;
 *   if (error) return <ErrorMessage />;
 *   return (
 *     <>
 *       <TodaySummaryCard {...data.today} />
 *       <WeekSummaryCard {...data.thisWeek} />
 *     </>
 *   );
 * }
 * ```
 */
export function useDashboard() {
  const query = useQuery({
    queryKey: dashboardKeys.summary(),
    queryFn: fetchDashboard,
    // Dashboard data can be cached for a short time
    staleTime: 1 * 60 * 1000, // 1 minute
    // Retry on errors (except auth errors)
    retry: (failureCount: number, error: Error) => {
      if (error instanceof ApiClientError && error.status === 401) {
        return false;
      }
      return failureCount < 3;
    },
    // Refetch on mount if stale
    refetchOnMount: true,
  });

  return {
    /** Dashboard data with today and week summaries */
    data: query.data ?? null,
    /** Whether the dashboard is being fetched for the first time (no cached data) */
    isLoading: query.isLoading,
    /** Whether the dashboard is currently being fetched (includes background refetches) */
    isFetching: query.isFetching,
    /** Whether data is being shown from the cache while a background update is in progress */
    isRefetching: query.isRefetching,
    /** Whether the fetch failed with an error */
    isError: query.isError,
    /** Error details if the fetch failed */
    error: query.error,
    /** Refetch the dashboard manually */
    refetch: query.refetch,
  };
}
